// Pure focus-cycling maths for window-level Tab containment.
//
// The OS contains Tab inside the focused window: Tab moves to the next
// focusable descendant, Shift+Tab to the previous, and both wrap at the ends so
// focus can never escape into the dock, the desktop, or another app.
//
// Only the index arithmetic lives here — the DOM query (which elements are
// focusable *right now*) stays in the component, because it needs a live
// document. This module is what is worth testing.

/**
 * CSS selector for natively focusable / explicitly focusable elements.
 *
 * `[tabindex]:not([tabindex="-1"])` covers opt-in containers; `-1` is excluded
 * because it means "programmatically focusable, not a Tab stop".
 * Disabled controls are filtered here; visibility must be checked against the
 * live layout by the caller (see `isFocusableCandidate`).
 */
export const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
].join(",");

/**
 * Index of the element Tab should land on next.
 *
 * @param {number} count      how many focusable elements the window has
 * @param {number} currentIndex index of the currently focused one, or -1 when
 *                              focus is not on any of them (title bar, window
 *                              body, document.body …)
 * @param {boolean} [backward] true for Shift+Tab
 * @returns {number} the target index, or -1 when there is nothing to focus
 */
export function nextFocusIndex(count, currentIndex, backward = false) {
  const n = Math.trunc(Number(count) || 0);
  if (!Number.isFinite(n) || n <= 0) return -1;

  let i = Math.trunc(Number(currentIndex));
  // Anything outside the list (including NaN and out-of-range) is "not on a
  // Tab stop", which enters the ring at the near end for the given direction.
  if (!Number.isFinite(i) || i < 0 || i >= n) i = -1;

  if (i === -1) return backward ? n - 1 : 0;
  return backward ? (i - 1 + n) % n : (i + 1) % n;
}

/**
 * Whether `el` is a real Tab stop: not disabled, not hidden, not
 * `aria-hidden`, and actually rendered.
 *
 * A hidden-but-present control would otherwise swallow a Tab stop and make the
 * trap look broken (Tab appears to do nothing).
 *
 * @param {Element | null | undefined} el
 * @returns {boolean}
 */
export function isFocusableCandidate(el) {
  if (!el) return false;
  /** @type {any} */
  const any = el;
  if (any.disabled) return false; // disabled controls are never Tab stops
  if (any.hidden) return false;
  if (typeof any.getAttribute === "function") {
    if (any.getAttribute("aria-hidden") === "true") return false;
    if (any.getAttribute("tabindex") === "-1") return false;
  }
  // Zero client rects === not rendered (display:none, detached, collapsed
  // ancestor). Cheaper and more reliable than reading computed styles.
  if (typeof any.getClientRects === "function" && any.getClientRects().length === 0) return false;
  return true;
}
